import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import VerificationTypeTestComponent from './VerificationTypeTestComponent';
import { emptyArray } from 'utils/constants';
import { TEST_VERIFICATION_FORMULA } from '../mutations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { VERIFICATION_FORMULA_GENERATED, TEST_FORMULA_VERIFICATION_FAILED } from '../subscriptions';
import { useToast } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_TYPES_QUERY, VERIFICATION_TYPE_QUERY } from '../queries';
import { useValidation } from '@totalsoft/pure-validations-react';
import { required, shape } from '@totalsoft/pure-validations';

const VerificationTypeTestContainer = ({ verificationTypeId }) => {
  const [verificationTestResult, setVerificationTestResult] = useState(emptyArray);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [verificationType, setVerificationType] = useState({});

  const [t] = useTranslation();
  const addToast = useToast();

  const [localState, setLocalState] = useState({
    documentNumber: undefined,
    documentTypeId: undefined
  });

  const validator = shape({
    documentNumber: required,
    documentTypeId: required
  });

  const [conditionValidateTesting, validateConditionTesting] = useValidation(validator);

  useQuery(DOCUMENT_TYPES_QUERY, {
    onCompleted: data => {
      setDocumentTypes(data.documentTypes);
    }
  });

  useQuery(VERIFICATION_TYPE_QUERY, {
    variables: {
      verificationTypeId: verificationTypeId
    },
    onCompleted: data => {
      setVerificationType(data.verificationType);
    },
    skip: !verificationTypeId
  });

  const [testVerificationFormula] = useMutation(TEST_VERIFICATION_FORMULA);

  useSubscription(TEST_FORMULA_VERIFICATION_FAILED, {
    onData: ({ data }) => {
      addToast(data.data.testFormulaVerificationFailed.errorMessage, 'error');
    }
  });

  useSubscription(VERIFICATION_FORMULA_GENERATED, {
    onData: ({ data: { data } }) => {
      const verificationType = data.verificationFormulaGenerated.verificationFormulas;
      setVerificationTestResult(verificationType);
      addToast(t('VerificationTypes.VerificationTestGenerated'), 'success');
    }
  });

  const handlePropertyChange = (propName, propValue) => {
    setLocalState(prevState => ({ ...prevState, [propName]: propValue }));
    setVerificationTestResult(emptyArray);
  };

  useEffect(() => {
    validateConditionTesting(localState);
  }, [localState, validateConditionTesting]);

  const handleVerificationClickTest = useCallback(() => {
    const input = {
      verificationTypeId,
      documentNumber: localState.documentNumber,
      documentTypeId: localState.documentTypeId
    };
    if (!validateConditionTesting(input)) return;
    setLocalState(prevState => ({ ...prevState, canTestVerification: true }));
    testVerificationFormula({
      variables: { input }
    });
  }, [verificationTypeId, localState.documentNumber, localState.documentTypeId, validateConditionTesting, testVerificationFormula]);

  return (
    <VerificationTypeTestComponent
      verificationTestResponse={verificationTestResult}
      documentTypes={documentTypes}
      documentTypeId={localState.documentTypeId}
      documentNumber={localState.documentNumber}
      onVerificationClickTest={handleVerificationClickTest}
      onPropertyChange={handlePropertyChange}
      verificationTypeId={verificationTypeId}
      validation={conditionValidateTesting}
      verificationType={verificationType}
    />
  );
};

VerificationTypeTestContainer.propTypes = {
  verificationTypeId: PropTypes.number.isRequired
};

export default VerificationTypeTestContainer;
